/**
 * Serviço para ações sobre ordens no Board (ex.: exclusão).
 */

import { getApiBaseUrl } from '../../../utils/apiConfig';

function authHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  };
}

/**
 * Exclui uma ordem de serviço (chama API que executa DELETE em TB02115 e TB02130).
 * @param {string} orderId - TB02115_CODIGO
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function deleteOrder(orderId) {
  const url = `${getApiBaseUrl()}/api/board/orders/${encodeURIComponent(orderId)}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      success: false,
      message: data.message || `Erro ${response.status}: ${response.statusText}`,
    };
  }
  return data;
}

/**
 * Confirma se a OS existe em TB02115.
 * @param {string} numeroOS
 * @returns {Promise<{ success: boolean, exists: boolean, codigo?: string|null, message?: string }>}
 */
export async function checkOrderExists(numeroOS) {
  const url = `${getApiBaseUrl()}/api/board/orders/${encodeURIComponent(numeroOS)}/exists`;
  const response = await fetch(url, { headers: authHeaders() });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      success: false,
      exists: false,
      message: data.message || `Erro ${response.status}`,
    };
  }
  return {
    success: true,
    exists: Boolean(data.exists),
    codigo: data.codigo || null,
  };
}

/**
 * Histórico do processo (TB02130) para o modal de situação da OS.
 * @param {string} numeroOS
 * @returns {Promise<{ success: boolean, data?: array, message?: string }>}
 */
export async function fetchHistoricoProcesso(numeroOS) {
  const url = `${getApiBaseUrl()}/api/board/orders/${encodeURIComponent(numeroOS)}/historico-processo`;
  const response = await fetch(url, { headers: authHeaders() });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    return {
      success: false,
      data: [],
      message: data.message || `Erro ${response.status}`,
    };
  }
  return {
    success: true,
    data: Array.isArray(data.data) ? data.data : [],
  };
}

/**
 * Ciclos da OS nativa (atendimento original + retornos), ciclo atual primeiro.
 * @param {string} numeroOS
 * @returns {Promise<{ currentCycle: number, returnCount: number, cycles: array }|null>}
 */
export async function fetchNativeOrderCycles(numeroOS) {
  const url = `${getApiBaseUrl()}/api/board/orders/${encodeURIComponent(numeroOS)}/ciclos`;
  const response = await fetch(url, { headers: authHeaders() });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) throw new Error(data.message || `Erro ${response.status}`);
  return data.data || null;
}

/**
 * Retorna OS liberada para pendência.
 * @param {string} numeroOS
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function returnOrderToPendente(numeroOS) {
  const url = `${getApiBaseUrl()}/api/board/orders/${encodeURIComponent(numeroOS)}/return-to-pendente`;
  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    return {
      success: false,
      message: data.message || `Erro ${response.status}`,
    };
  }
  return {
    success: true,
    message: data.message || 'OS retornada para pendência com sucesso.',
    data: data.data,
  };
}

/**
 * Anexos Customers da OS (URLs opacas).
 * @param {string} numeroOS
 * @returns {Promise<{ success: boolean, data?: array, message?: string }>}
 */
export async function fetchOrderAttachments(numeroOS) {
  const url = `${getApiBaseUrl()}/api/board/orders/${encodeURIComponent(numeroOS)}/attachments`;
  const response = await fetch(url, { headers: authHeaders() });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    return {
      success: false,
      data: [],
      message: data.message || `Erro ${response.status}`,
    };
  }
  return {
    success: true,
    data: Array.isArray(data.data) ? data.data : [],
  };
}

/**
 * Transcreve áudio do defeito e grava nas observações gerais da OS.
 * @param {string} numeroOS
 * @returns {Promise<{ success: boolean, data?: { text: string, observacaoGeral: string }, message?: string }>}
 */
export async function transcribeOrderDefectAudio(numeroOS) {
  const url = `${getApiBaseUrl()}/api/board/orders/${encodeURIComponent(numeroOS)}/attachments/transcribe-audio`;
  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    return {
      success: false,
      message: data.message || `Erro ${response.status}`,
    };
  }
  return {
    success: true,
    data: data.data || null,
  };
}
